/**
 * The formatted text of a `document` analyte (a urine culture's report, a cytology note): a small
 * HTML subset kept in `result_value.value_text` (ADR 0019).
 *
 * One parser turns ANY input — what the editor produced, what was pasted from Word, what the
 * previous system stored — into a closed model (paragraphs and lists of formatted runs). The
 * stored HTML is that model serialised, so the allow-list is structural: nothing outside
 * p · br · strong · em · u · sup · sub · ol · ul · li and `style="text-align:…"` can come out,
 * whatever came in. The server runs it on write; the report and every screen run it again on
 * read, so rows written before this module (the migrated history) are safe too.
 *
 * Pure TypeScript with no DOM, so the API, the PDF renderer and the web app share it.
 */

export type RichAlign = "left" | "center" | "right" | "justify";

export interface RichRun {
  /** "\n" alone is a line break. */
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  script: "sup" | "sub" | null;
}

export interface RichParagraph {
  type: "p";
  align: RichAlign;
  runs: RichRun[];
}

export interface RichList {
  type: "list";
  ordered: boolean;
  items: { align: RichAlign; runs: RichRun[] }[];
}

export type RichBlock = RichParagraph | RichList;

/** Longest stored document, in characters of sanitised HTML (the migrated ones top out near 2 500). */
export const RICH_TEXT_MAX = 20_000;

// -- tokeniser -------------------------------------------------------------------------------------

type Token = { t: "text"; v: string } | { t: "open"; name: string; attrs: string; selfClosing: boolean } | { t: "close"; name: string };

const TOKEN = /<!--[\s\S]*?(?:-->|$)|<![^>]*>?|<\?[^>]*>?|<\/([a-zA-Z][\w:-]*)[^>]*>?|<([a-zA-Z][\w:-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>?/g;

function tokenise(html: string): Token[] {
  const out: Token[] = [];
  let last = 0;
  for (const m of html.matchAll(TOKEN)) {
    if (m.index > last) out.push({ t: "text", v: html.slice(last, m.index) });
    last = m.index + m[0].length;
    if (m[1]) out.push({ t: "close", name: m[1].toLowerCase() });
    else if (m[2]) {
      const attrs = m[3]!;
      out.push({ t: "open", name: m[2].toLowerCase(), attrs, selfClosing: attrs.trimEnd().endsWith("/") });
    }
    // comments, doctypes and processing instructions are dropped
  }
  if (last < html.length) out.push({ t: "text", v: html.slice(last) });
  return out;
}

const NAMED: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", ndash: "–", mdash: "—", hellip: "…",
  lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”", bull: "•", middot: "·", deg: "°", plusmn: "±", micro: "µ",
  times: "×", divide: "÷", le: "≤", ge: "≥", sup1: "¹", sup2: "²", sup3: "³", frac12: "½", frac14: "¼", frac34: "¾",
  iexcl: "¡", iquest: "¿", laquo: "«", raquo: "»", ordf: "ª", ordm: "º", copy: "©", reg: "®", euro: "€",
};
// Latin-1 letters with accents: &aacute; &Ntilde; &ouml; …
const MARKS: Record<string, string> = { acute: "́", grave: "̀", circ: "̂", tilde: "̃", uml: "̈", cedil: "̧", ring: "̊" };

function decodeEntities(s: string): string {
  return s.replace(/&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g, (whole, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : "";
    }
    const named = NAMED[body];
    if (named !== undefined) return named;
    const m = /^([a-zA-Z])(acute|grave|circ|tilde|uml|cedil|ring)$/.exec(body);
    if (m) return (m[1]! + MARKS[m[2]!]).normalize("NFC");
    if (body === "szlig") return "ß";
    return whole;
  });
}

// -- parser ------------------------------------------------------------------------------------------

/** Elements whose content is never text for the reader: dropped whole. */
const DROP = new Set(["script", "style", "iframe", "object", "embed", "noscript", "template", "textarea", "title", "head", "svg", "math", "select", "button", "xmp", "noembed", "noframes"]);
const VOID = new Set(["br", "img", "hr", "input", "meta", "link", "area", "base", "col", "wbr", "source", "track", "param"]);
/** Elements that start a new paragraph. */
const BLOCK = new Set(["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "section", "article", "header", "footer", "center", "tr", "table", "address", "figure", "dd", "dt", "hr"]);

type Format = "bold" | "italic" | "underline" | "sup" | "sub";
const FORMAT_TAGS: Record<string, Format> = { strong: "bold", b: "bold", em: "italic", i: "italic", u: "underline", ins: "underline", sup: "sup", sub: "sub" };

function styleProps(attrs: string): Map<string, string> {
  const m = /\bstyle\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
  const out = new Map<string, string>();
  if (!m) return out;
  for (const decl of decodeEntities(m[1] ?? m[2] ?? m[3]!).split(";")) {
    const i = decl.indexOf(":");
    if (i > 0) out.set(decl.slice(0, i).trim().toLowerCase(), decl.slice(i + 1).trim().toLowerCase());
  }
  return out;
}

function alignOf(name: string, attrs: string): RichAlign | null {
  if (name === "center") return "center";
  const v = styleProps(attrs).get("text-align") ?? /\balign\s*=\s*["']?(\w+)/i.exec(attrs)?.[1]?.toLowerCase();
  if (v === "center" || v === "right" || v === "justify" || v === "left") return v;
  return null;
}

/** Formats a <span style> (or any element's style) switches on: what Word and old editors emit. */
function styleFormats(attrs: string): Format[] {
  const p = styleProps(attrs);
  const out: Format[] = [];
  const weight = p.get("font-weight");
  if (weight === "bold" || weight === "bolder" || (weight !== undefined && Number(weight) >= 600)) out.push("bold");
  if (p.get("font-style") === "italic") out.push("italic");
  if ((p.get("text-decoration") ?? p.get("text-decoration-line") ?? "").includes("underline")) out.push("underline");
  const va = p.get("vertical-align");
  if (va === "super") out.push("sup");
  else if (va === "sub") out.push("sub");
  return out;
}

interface Open {
  name: string;
  formats: Format[];
}

/** Parse any HTML (or plain text) into the closed model. Never throws. */
export function parseRichText(input: string | null | undefined): RichBlock[] {
  const blocks: RichBlock[] = [];
  const fmt: Record<Format, number> = { bold: 0, italic: 0, underline: 0, sup: 0, sub: 0 };
  const stack: Open[] = [];
  let drop: string | null = null;
  let dropDepth = 0;
  let para: RichParagraph | null = null;
  let list: RichList | null = null;
  let listDepth = 0;
  let item: { align: RichAlign; runs: RichRun[] } | null = null;
  let pendingAlign: RichAlign | null = null;

  const target = (): RichRun[] => {
    if (list && listDepth > 0) {
      if (!item) {
        item = { align: "left", runs: [] };
        list.items.push(item);
      }
      return item.runs;
    }
    if (!para) {
      para = { type: "p", align: pendingAlign ?? "left", runs: [] };
      blocks.push(para);
    }
    return para.runs;
  };
  const push = (text: string) => {
    const runs = target();
    const run: RichRun = { text, bold: fmt.bold > 0, italic: fmt.italic > 0, underline: fmt.underline > 0, script: fmt.sup > 0 ? "sup" : fmt.sub > 0 ? "sub" : null };
    const prev = runs[runs.length - 1];
    if (prev && text !== "\n" && prev.text !== "\n" && sameFormat(prev, run)) prev.text += text;
    else runs.push(run);
  };
  const endParagraph = () => {
    para = null;
  };
  const lineBreakInItem = () => {
    if (item && item.runs.length > 0) push("\n");
  };

  for (const tok of tokenise(input ?? "")) {
    if (drop) {
      if (tok.t === "open" && tok.name === drop && !tok.selfClosing) dropDepth++;
      else if (tok.t === "close" && tok.name === drop && --dropDepth === 0) drop = null;
      continue;
    }
    if (tok.t === "text") {
      const text = decodeEntities(tok.v).replace(/[\t\n\r\f ]+/g, " ");
      const inItem = list !== null && listDepth > 0;
      if (text === " " && !(inItem ? item : para)) continue; // whitespace between blocks
      push(text);
      continue;
    }
    const name = tok.name;
    if (tok.t === "open") {
      if (DROP.has(name)) {
        if (!tok.selfClosing) {
          drop = name;
          dropDepth = 1;
        }
        continue;
      }
      if (name === "br") {
        push("\n");
        continue;
      }
      if (name === "ol" || name === "ul") {
        endParagraph();
        if (!list || listDepth === 0) {
          list = { type: "list", ordered: name === "ol", items: [] };
          blocks.push(list);
        }
        listDepth++;
        item = null;
        stack.push({ name, formats: [] });
        continue;
      }
      if (name === "li") {
        if (!list || listDepth === 0) {
          endParagraph();
          list = { type: "list", ordered: false, items: [] };
          blocks.push(list);
          listDepth = 1;
          stack.push({ name: "ul", formats: [] });
        }
        item = { align: alignOf(name, tok.attrs) ?? "left", runs: [] };
        list.items.push(item);
        stack.push({ name, formats: [] });
        continue;
      }
      const formats = [...(FORMAT_TAGS[name] ? [FORMAT_TAGS[name]] : []), ...styleFormats(tok.attrs)];
      if (BLOCK.has(name)) {
        if (list && listDepth > 0) lineBreakInItem();
        else {
          // The paragraph opens with its first content, so wrappers (<div><p>…) make no blank line.
          endParagraph();
          pendingAlign = alignOf(name, tok.attrs);
        }
      }
      if (VOID.has(name) || tok.selfClosing) continue;
      for (const f of formats) fmt[f]++;
      stack.push({ name, formats });
      continue;
    }
    // close tag
    const at = stack.map((o) => o.name).lastIndexOf(name);
    if (at < 0) continue;
    while (stack.length > at) {
      const o = stack.pop()!;
      for (const f of o.formats) fmt[f]--;
      if (o.name === "li") item = null;
      else if (o.name === "ol" || o.name === "ul") {
        listDepth--;
        item = null;
        if (listDepth === 0) list = null;
      } else if (BLOCK.has(o.name) && !(list && listDepth > 0)) {
        endParagraph();
        pendingAlign = null;
      }
    }
  }
  return normalise(blocks);
}

function sameFormat(a: RichRun, b: RichRun): boolean {
  return a.bold === b.bold && a.italic === b.italic && a.underline === b.underline && a.script === b.script;
}

/** Trim each block's edges, drop a trailing break, collapse doubled spaces across runs, drop empty blocks. */
function normalise(blocks: RichBlock[]): RichBlock[] {
  const clean = (runs: RichRun[]): RichRun[] => {
    const out: RichRun[] = [];
    let lastSpace = true; // at block start a space is dropped
    for (const r of runs) {
      if (r.text === "\n") {
        const prev = out[out.length - 1];
        if (prev && prev.text !== "\n") prev.text = prev.text.replace(/ +$/, "");
        out.push(r);
        lastSpace = true;
        continue;
      }
      let text = r.text;
      if (lastSpace) text = text.replace(/^ +/, "");
      if (!text) continue;
      lastSpace = text.endsWith(" ");
      const prev = out[out.length - 1];
      if (prev && prev.text !== "\n" && sameFormat(prev, r)) prev.text += text;
      else out.push({ ...r, text });
    }
    const last = out[out.length - 1];
    if (last && last.text !== "\n") {
      last.text = last.text.replace(/ +$/, "");
      if (!last.text) out.pop();
    }
    // A break that ends a block draws nothing in HTML; keep it only when it is the block's sole content.
    if (out.length > 1 && out[out.length - 1]!.text === "\n") out.pop();
    return out;
  };
  const out: RichBlock[] = [];
  for (const b of blocks) {
    if (b.type === "p") {
      const runs = clean(b.runs);
      out.push({ ...b, runs });
    } else {
      const items = b.items.map((i) => ({ align: i.align, runs: clean(i.runs) })).filter((i) => i.runs.length > 0);
      if (items.length > 0) out.push({ ...b, items });
    }
  }
  // Blank paragraphs at either end carry nothing; inside, they are the blank lines the author typed.
  const blank = (b: RichBlock | undefined) => b?.type === "p" && b.runs.every((r) => r.text === "\n");
  while (out.length > 0 && blank(out[0])) out.shift();
  while (out.length > 0 && blank(out[out.length - 1])) out.pop();
  return out;
}

// -- serialiser --------------------------------------------------------------------------------------

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/ /g, "&nbsp;");
}

function runsHtml(runs: RichRun[]): string {
  if (runs.length === 0) return "<br>";
  return runs
    .map((r) => {
      if (r.text === "\n") return "<br>";
      let h = escapeText(r.text);
      if (r.script) h = `<${r.script}>${h}</${r.script}>`;
      if (r.underline) h = `<u>${h}</u>`;
      if (r.italic) h = `<em>${h}</em>`;
      if (r.bold) h = `<strong>${h}</strong>`;
      return h;
    })
    .join("");
}

const alignAttr = (a: RichAlign) => (a === "left" ? "" : ` style="text-align:${a}"`);

/** The model as the stored HTML subset. */
export function serializeRichText(blocks: RichBlock[]): string {
  return blocks
    .map((b) => {
      if (b.type === "p") return `<p${alignAttr(b.align)}>${runsHtml(b.runs)}</p>`;
      const tag = b.ordered ? "ol" : "ul";
      return `<${tag}>${b.items.map((i) => `<li${alignAttr(i.align)}>${runsHtml(i.runs)}</li>`).join("")}</${tag}>`;
    })
    .join("");
}

/** Any HTML in, the allow-listed subset out; "" when nothing readable is left. */
export function sanitizeRichText(input: string | null | undefined): string {
  const blocks = parseRichText(input);
  return richTextToPlain(blocks) === "" ? "" : serializeRichText(blocks);
}

/** Plain text of a document: blocks on their own lines, list items numbered or bulleted. */
export function richTextToPlain(input: string | RichBlock[] | null | undefined): string {
  const blocks = typeof input === "object" && input !== null ? input : parseRichText(input);
  const text = (runs: RichRun[]) => runs.map((r) => r.text).join("");
  const lines: string[] = [];
  for (const b of blocks) {
    if (b.type === "p") lines.push(text(b.runs));
    else b.items.forEach((i, n) => lines.push(`${b.ordered ? `${n + 1}.` : "•"} ${text(i.runs)}`));
  }
  return lines.join("\n").replace(/ /g, " ").trim();
}
